/**
 * System Downtime Analytics Dashboard
 * Charts and metrics for analyzing system downtime data
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  Clock, AlertTriangle, CheckCircle, Users, TrendingDown, RefreshCw,
  Loader2, Calendar, Activity, Timer, BarChart3
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { AnKpiCard, AnFilterBar, AnFilterField, AnStatusPill, AnAvatar, AnEmptyState } from './ui/AnalyticsUi';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../ui/select';
import { toast } from 'sonner';
import { useAuth } from '../../App';
import { formatDateOnly } from '../../utils/formatters';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Colors for charts
const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

const DowntimeAnalytics = ({ showHeader = true }) => {
  // Get user from auth context
  const { user: currentUser } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');
  const [summary, setSummary] = useState(null);
  const [byReason, setByReason] = useState([]);
  const [dailyData, setDailyData] = useState([]);
  const [byUser, setByUser] = useState([]);
  const [distribution, setDistribution] = useState([]);
  const [recentIncidents, setRecentIncidents] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryRes, reasonRes, dailyRes, userRes, distRes, recentRes] = await Promise.all([
        axios.get(`${API}/api/downtime-analytics/summary?days=${period}`),
        axios.get(`${API}/api/downtime-analytics/by-reason?days=${period}`),
        axios.get(`${API}/api/downtime-analytics/by-day?days=${period}`),
        axios.get(`${API}/api/downtime-analytics/by-user?days=${period}`),
        axios.get(`${API}/api/downtime-analytics/resolution-times?days=${period}`),
        axios.get(`${API}/api/downtime-analytics/recent-incidents?limit=5`)
      ]);

      setSummary(summaryRes.data);
      setByReason(reasonRes.data.by_reason || []);
      setDailyData(dailyRes.data.daily_data || []);
      setByUser(userRes.data.by_user || []);
      setDistribution(distRes.data.distribution || []);
      setRecentIncidents(recentRes.data.recent_incidents || []);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatMinutes = (mins) => {
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    const remaining = mins % 60;
    return remaining > 0 ? `${hours}h ${remaining}m` : `${hours}h`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="downtime-analytics">
      {/* Header — title hidden inside the Analytics module (layout renders it);
          controls (period + refresh) always shown. */}
      <div className={`flex items-center gap-4 ${showHeader ? 'justify-between' : ''}`}>
        {showHeader && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-blue-600" />
              Downtime Analytics
            </h2>
            <p className="text-gray-500 mt-1">System downtime metrics and trends</p>
          </div>
        )}
        <AnFilterBar testId="downtime-filter-bar" className="w-full" actions={
          <Button variant="outline" onClick={fetchData} data-testid="refresh-btn" className="an-btn h-11 px-4 rounded-xl text-[13px] font-semibold"><RefreshCw className="w-4 h-4 mr-1.5" />Refresh</Button>
        }>
          <AnFilterField icon={Calendar} label="Period" className="min-w-[180px]">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger data-testid="period-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="14">Last 14 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="60">Last 60 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
          </AnFilterField>
        </AnFilterBar>
      </div>

      {/* Summary Stats */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="summary-stats">
          <AnKpiCard icon={AlertTriangle} tone="blue" label="Total Incidents" value={summary.total_incidents} testid="dt-kpi-incidents" delay={0} />
          <AnKpiCard icon={Clock} tone="red" label="Total Downtime" display={formatMinutes(summary.total_downtime_minutes)} testid="dt-kpi-downtime" delay={50} />
          <AnKpiCard icon={Timer} tone="amber" label="Avg Resolution" display={formatMinutes(summary.avg_resolution_time_minutes)} testid="dt-kpi-resolution" delay={100} />
          <AnKpiCard icon={Users} tone="green" label="Affected Users" value={summary.affected_users} testid="dt-kpi-users" delay={150} />
        </div>
      )}

      {/* Charts Row 1 */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Daily Trend */}
        <Card className="an-rise rounded-2xl border-slate-200/80 shadow-[0_1px_2px_rgba(15,23,42,.04),0_8px_24px_-14px_rgba(15,23,42,.14)]" data-testid="daily-trend-chart">
          <CardHeader>
            <CardTitle className="text-sm font-semibold text-slate-900">Daily Incident Trend</CardTitle>
            <CardDescription>Number of incidents per day</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={dailyData.slice(-14)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 11 }}
                  // eslint-disable-next-line no-restricted-syntax -- DATE-DISPLAY-001: intentional chart axis tick
                  tickFormatter={(d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip 
                  labelFormatter={(d) => formatDateOnly(d)}
                  formatter={(value) => [value, 'Incidents']}
                />
                <Line 
                  type="monotone" 
                  dataKey="incidents" 
                  stroke="#3B82F6" 
                  strokeWidth={2}
                  dot={{ fill: '#3B82F6', strokeWidth: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* By Reason */}
        <Card className="an-rise rounded-2xl border-slate-200/80 shadow-[0_1px_2px_rgba(15,23,42,.04),0_8px_24px_-14px_rgba(15,23,42,.14)]" data-testid="by-reason-chart">
          <CardHeader>
            <CardTitle className="text-sm font-semibold text-slate-900">Incidents by Reason</CardTitle>
            <CardDescription>Distribution of downtime causes</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={byReason}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ reason, percent }) => `${reason} (${(percent * 100).toFixed(0)}%)`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="incidents"
                  nameKey="reason"
                >
                  {byReason.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [value, 'Incidents']} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Resolution Time Distribution */}
        <Card className="an-rise rounded-2xl border-slate-200/80 shadow-[0_1px_2px_rgba(15,23,42,.04),0_8px_24px_-14px_rgba(15,23,42,.14)]" data-testid="resolution-distribution-chart">
          <CardHeader>
            <CardTitle className="text-sm font-semibold text-slate-900">Resolution Time Distribution</CardTitle>
            <CardDescription>How quickly issues are resolved</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={distribution}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="bucket" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#10B981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Top Users by Downtime */}
        <Card className="an-rise rounded-2xl border-slate-200/80 shadow-[0_1px_2px_rgba(15,23,42,.04),0_8px_24px_-14px_rgba(15,23,42,.14)]" data-testid="by-user-chart">
          <CardHeader>
            <CardTitle className="text-sm font-semibold text-slate-900">Users with Most Downtime</CardTitle>
            <CardDescription>Top users by total downtime minutes</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={byUser} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis 
                  dataKey="user_name" 
                  type="category" 
                  tick={{ fontSize: 10 }}
                  width={100}
                />
                <Tooltip formatter={(value) => [formatMinutes(value), 'Downtime']} />
                <Bar dataKey="total_minutes" fill="#8B5CF6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Recent Incidents */}
      <Card className="an-rise rounded-2xl border-slate-200/80 shadow-[0_1px_2px_rgba(15,23,42,.04),0_8px_24px_-14px_rgba(15,23,42,.14)]" data-testid="recent-incidents">
        <CardHeader>
          <CardTitle className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Recent Incidents
          </CardTitle>
          <CardDescription>Latest reported system downtime</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {recentIncidents.length === 0 ? (
              <AnEmptyState tone="ok" title="No incidents recorded" subtitle="No downtime reported in this period." testId="dt-no-incidents" />
            ) : (
              recentIncidents.map((incident, idx) => (
                <div
                  key={incident.id || idx}
                  className="an-row flex items-center justify-between p-3 bg-slate-50/60 rounded-xl border border-slate-100"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${
                      incident.status === 'active' ? 'bg-red-500 animate-pulse' : 'bg-green-500'
                    }`} />
                    <AnAvatar name={incident.user_name} tone={incident.status === 'active' ? 'emerald' : 'indigo'} />
                    <div>
                      <p className="text-[13px] font-semibold text-slate-900">{incident.user_name}</p>
                      <p className="text-[12px] text-slate-500">{incident.reason_name}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <AnStatusPill tone={incident.status === 'active' ? 'red' : 'green'} dot>{incident.status}</AnStatusPill>
                    {incident.duration_minutes && (
                      <p className="text-xs text-gray-400 mt-1">
                        {formatMinutes(incident.duration_minutes)}
                      </p>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DowntimeAnalytics;
